import { Button, Container, Paper, Text, Flex, Badge } from "@mantine/core";
import { useForm } from "react-hook-form";
import { Select, TextInput } from "react-hook-form-mantine";
import { useContext, useEffect } from "react";
import { IconInfoCircle } from "@tabler/icons-react";

import { AuthContext } from "../../services/context";
import { getCurrentWorkspace } from "../../services/userConfig";
import { useCreateTag, useGetTagColors } from "../../api/tag";
import subtypeOptions from "./utils/subtypeOptions";
import defaultColors from "./utils/defaultColors";
import { useGetEvent } from "../../api/event";
import LocalizedInput from "../common/LocalizedInput";
import { web_tagCreateSchema, FEATURE_FLAGS } from "@congrs/schemas";
import resolver from "@congrs/resolver";
import { makeI18nDefaultValue } from "../../utils/i18nUtils";
import { useIsFeatureEnabled } from "../../api/featureFlags";

function CreateTag() {
  const { user } = useContext(AuthContext);
  const currentWorkspace = getCurrentWorkspace(user);
  const currentEventId = currentWorkspace.eventId;

  const { data: event } = useGetEvent(currentEventId);
  const { mutate, isPending, isError, error } = useCreateTag();
  const { data: tagColors } = useGetTagColors(currentEventId);
  const interestTagsEnabled = useIsFeatureEnabled(
    currentEventId,
    FEATURE_FLAGS.ATTENDEE_INTERESTS_TAGS
  );

  const mainLanguage = event?.language || "en";
  const otherLanguages = event?.secondaryLanguages || [];

  const getDefaultValues = () => ({
    type: null,
    subtype: "",
    titleI18n: makeI18nDefaultValue(mainLanguage, otherLanguages),
    categoryOfUser: [],
  });

  const defaultValues = getDefaultValues();

  const {
    control,
    watch,
    handleSubmit,
    clearErrors,
    resetField,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues,
    resolver: resolver(web_tagCreateSchema),
  });

  const type = watch("type");
  const subtype = watch("subtype");
  const isCategoryOfUserSubtype =
    type === "people" && subtype === "categoryOfUser";

  useEffect(() => {
    if (!type) {
      resetField("subtype");
    }
  }, [type, resetField]);

  // Reset form with proper i18n defaults when event languages are loaded
  useEffect(() => {
    if (event) {
      reset(getDefaultValues());
    }
  }, [event?.language, event?.secondaryLanguages]);

  const previewColor =
    (Array.isArray(tagColors) &&
      tagColors?.find((color) => color.subtype === subtype)?.color) ||
    defaultColors.find((color) => color.subtype === subtype)?.color;

  const onSubmit = (data) => {
    // Clear previous errors
    clearErrors();

    const newData = {
      ...data,
      eventId: currentEventId,
    };

    mutate(newData, {
      onSuccess: () => {
        reset(getDefaultValues());
      },
    });
  };

  return (
    <Container size={500}>
      <Paper withBorder shadow="md" p={16} mt={16} radius="md">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Select
            label="Type"
            placeholder="Type"
            name="type"
            withAsterisk
            control={control}
            clearable
            data={[
              { label: "People", value: "people" },
              { label: "Session", value: "session" },
            ]}
            mt="md"
          />
          {type && (
            <Select
              label="Subtype"
              placeholder="Select type to see subtype options"
              name="subtype"
              withAsterisk
              control={control}
              data={subtypeOptions(type, { interestTagsEnabled })}
              mt="md"
            />
          )}

          {type && subtype && (
            <>
              <Paper shadow="md" p="sm" withBorder mt="xs">
                <Flex
                  style={{
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <Text style={{ fontSize: 13, paddingInlineEnd: 5 }}>
                    Preview of color for subtype selected:
                  </Text>
                  <Badge color={previewColor} variant="light">
                    tag
                  </Badge>
                </Flex>
                <Text style={{ fontSize: 13 }}>
                  Configure color for subtypes of tags in Color tags section.
                </Text>
              </Paper>
              {isCategoryOfUserSubtype && (
                <Paper shadow="md" p="sm" withBorder mt="xs">
                  <Flex style={{ alignItems: "center" }}>
                    <IconInfoCircle />
                    <Container>
                      <Text style={{ fontSize: 13 }}>
                        Default category of user tags are already created:
                      </Text>
                      {["Attendee", "Speaker", "Sponsor", "Organizer"].map(
                        (tag) => (
                          <Badge
                            color={previewColor}
                            variant="light"
                            key={tag}
                            mr={5}
                          >
                            {tag}
                          </Badge>
                        )
                      )}
                    </Container>
                  </Flex>
                </Paper>
              )}
            </>
          )}

          {/* Localized single-title editor */}
          {type && subtype && event && (
            <LocalizedInput
              control={control}
              fields={[
                {
                  nameBase: "titleI18n",
                  label: "Title",
                  component: TextInput,
                  props: {
                    mt: "sm",
                    placeholder: "Localized title",
                    withAsterisk: true,
                  },
                },
              ]}
              mainLanguage={event?.language}
              otherLanguages={event?.secondaryLanguages}
            />
          )}

          {/* Show form validation errors */}
          {errors.titleI18n && (
            <Text c="red" size="sm" mt="md">
              {errors.titleI18n.message}
            </Text>
          )}
          {/* Show api errors */}
          {isError && (
            <Text c="red" size="sm" mt="md">
              {error.message.includes("Duplicate")
                ? "A tag with this title already exists. Please choose a different title."
                : "An error occurred: " + error.message}
            </Text>
          )}

          <Button
            type="submit"
            mt="md"
            loading={isPending}
            disabled={isPending}
          >
            Create tag
          </Button>
        </form>
      </Paper>
    </Container>
  );
}

export default CreateTag;
